import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Value } from "baseui/select";
import { colors, glyphs, useMediaQuery } from "../../../Styles";
import { CheckBox, SelectField } from "../..";
import { field, FilterContent, FilterDisplayType, FilterDisplayEnum, } from "../../../Definitions/filterTypes";

import { ReportTemplate } from "../../../Assets/TestData/reportTemplates";
import { AnimalGroupData } from "../../../Assets/TestData/animalGroups";
import { ConditionData } from "../../../Assets/TestData/conditions";
import { FieldTypes } from "../../../Definitions";

// #region | Data: filter Parameters
export const filterParameterTypes: FilterDisplayType[] = [
  {id: 1, label: "groupId", displayType: FilterDisplayEnum.select, options: AnimalGroupData.map(group => ({
    label: group.groupName,
    id: group.groupId
  })) },
  {id: 2, label: "conditionId", displayType: FilterDisplayEnum.select, options: ConditionData.map(condition => ({
    label: condition.conditionName,
    id: condition.conditionId
  }))},
  {id: 3, label: "locationId", displayType: FilterDisplayEnum.range}
]
// #endregion

interface Lvl2_Props {
  report?: ReportTemplate
}

export default function ReportModalLvl2({report}:Lvl2_Props) {
  const {t} = useTranslation()
  const mobileXs = useMediaQuery(`(max-width: 690px)`);
  const [selectFields, setSelectFields] = useState<field[]>([]);

  useEffect(() => {
    const filterParameters = report?.filterParameter.flatMap((parameter) => (Object.entries(parameter)
      .map(([filterLabelKey, value]) => {
        const id = filterParameterTypes.find((filterType) => (filterType.label === filterLabelKey))?.id
        return {
          id,
          value,
        }
      }))) || [];
    if (filterParameters) {
      setSelectFields([...filterParameters,{id:-1}])
    }
  },[report?.filterParameter])
  
  // SelectField Options
  const filterParamOptions = filterParameterTypes.map((type) => ({label: t(`report.filterParameter.${type.label}`, {defaultValue: type.label}), id: type.id}));
  // Handle onChange on SelectField
  const handleFilterTypeChange = (index: number, value: Value) => {
    const updatedSelectFields = [...selectFields];
    if (value.length > 0) {

      if (updatedSelectFields[index].id === -1) {
        updatedSelectFields.push({id: -1})
      }
      updatedSelectFields[index] = {id: value[0].id ? +value[0].id : 0, value: undefined};
    } else {
      updatedSelectFields[index].id = undefined;
    }
    setSelectFields(updatedSelectFields);
  };

  const handleFilterValueChange = (index?: number, value?: Value) => {
    const updatedSelectFields = [...selectFields];
    if (index && value && value.length > 0) {
      updatedSelectFields[index].value = value[0].id ? +value[0].id : 0;
    }
    setSelectFields(updatedSelectFields);
  }

  // Function to remove a SelectField by index
  const handleRemoveField = useCallback((index: number) => {
    const oldSelectedFields = [...selectFields];
    oldSelectedFields.splice(index, 1)
    setSelectFields(oldSelectedFields)
  },[selectFields]);

  const renderedFields = selectFields.map((field, index) => {return (
    <div 
      key={index} 
      style={{ 
        width: "100%", 
        display: "flex", 
        flexDirection: "column", 
        gap: "10px", 
        marginBottom: index === selectFields.length - 1 ? "0px" : "20px"
    }}>
      <div style={{ display: "flex", flexDirection: mobileXs ? "column" : "row", gap: mobileXs ? "10px" : "20px", alignItems: "start", justifyContent: "space-between" }}>
      <SelectField
          placeholder={t("select")+"..."}
          key={index+String(field.id)}
           
          options={filterParamOptions}
          onChange={(value) => handleFilterTypeChange(index, value)}
          defaultValue={filterParamOptions.find((parameter) => {
            return parameter.id === field.id
          })?.label}
          overrides={{
            fieldType: FieldTypes.primaryInputField,
            textStart: true
          }}
        />
        {field.id !== -1 && (
          <div style={{display: "flex", flexDirection: "row", alignItems: "end", justifyContent: "center", gap: "20px"}}>
            <CheckBox text={t("reports.showInReport")}/>
            <div style={{cursor: "pointer"}} onClick={() => handleRemoveField(index)}>
              <glyphs.Delete size="25px"/>
            </div>
          </div>
        )}
      </div>
      {/* Content based on selected filter parameter */}
      {field.id !== -1 && (
        <FilterContent filter={field} index={index} filterParameterTypes={filterParameterTypes} handleFilterValueChange={handleFilterValueChange} />
      )}
    </div>
  )})
  // #endregion
  
  return(
        <div style={{ width: mobileXs? "290px" : "600px" }}>
          <div style={{
            borderTop: "2px solid " + colors.blueDarkSecondary,
            borderBottom: "2px solid " + colors.blueDarkSecondary,
            padding: mobileXs? "10px 0px" : "10px",
            maxHeight: mobileXs ? "40vh" : "50vh",
            overflowY: "scroll",
            overflowX: "hidden",
            scrollbarWidth: "thin",
            scrollbarColor: colors.blueDarkSecondary+colors.transparent,
            display: "flex",
            alignItems: "center",
            justifyContent: "start",
            flexDirection: "column",
            gap: "10px"
          }}>
            {renderedFields}
          </div>
        </div>
  )
}